/**
 * @typedef {Object} FetchSignalPayload
 * @property {boolean} offer - Whether the current flow is an offer flow.
 */

/**
 * @typedef {FetchSignalPayload & { triggeredAt: string }} FetchSignalEventDetail
 * @property {string} triggeredAt - ISO timestamp when the signal was emitted.
 */

/**
 * Event-based utility used to broadcast API-start signals
 * and coordinate active request cancellation.
 */

class FetchSignal {
  /**
   * Shared event name emitted for both essential and marketing event listeners.
   * @type {string}
   */
  static SIGNAL_NAME = 'START_API_CALL';

  /**
   * Creates a new FetchSignal instance.
   */
  constructor() {
    this.eventTarget = new EventTarget();
    /** @type {AbortController|null} */
    this.currentAbortController = null;
    /** @type {FetchSignalEventDetail|null} */
    this.lastPayload = null;
  }

  /**
   * Emits an internal signal event enriched with trigger timestamp.
   *
   * @private
   * @param {string} eventName - Name of the event to dispatch.
   * @param {FetchSignalPayload} payload - Event payload.
   * @returns {void}
   */
  #emit(eventName, payload) {
    const detail = { ...payload, triggeredAt: new Date().toISOString() };
    this.lastPayload = detail;

    const event = new CustomEvent(eventName, { detail });
    this.eventTarget.dispatchEvent(event);
  }

  /**
   * Subscribes to an internal event and passes only the detail object to handler.
   *
   * @private
   * @param {string} eventName - Name of the event to listen to.
   * @param {(detail: FetchSignalEventDetail) => void} handler - Callback executed on event.
   * @returns {()=>void} unsubscribe function
   */
  #subscribe(eventName, handler) {
    const wrapper = (e) => {
      if (e && e.detail) {
        handler(e.detail);
      } else {
        handler(e);
      }
    };
    this.eventTarget.addEventListener(eventName, wrapper);
    return () => this.eventTarget.removeEventListener(eventName, wrapper);
  }

  /**
   * Core registration logic for signals to avoid DRY violations.
   * @private
   * @param {(detail: FetchSignalEventDetail) => void} handler
   * @returns {()=>void} unsubscribe function
   */
  #registerSignalListeners(handler) {
    if (this.lastPayload) {
      handler(this.lastPayload);
    }
    return this.#subscribe(FetchSignal.SIGNAL_NAME, handler);
  }

  /**
   * Registers a listener for essential or marketing events.
   *
   * @param {(detail: FetchSignalEventDetail) => void} handler - Listener callback
   * @returns {() => void} unsubscribe function
   * @example
   * const unsubscribe = fetchSignal.onSignalReady(({ offer }) => {
   *   if (!offer) log('BAU flow started');
   * });
   */
  // TODO: Once intl shop scripts are updated to dxt-vendor-shared-scripts,
  // split this into separate methods: onEssentialSignalReady and
  // onMarketingSignalReady. This will eliminate the ambiguity around the
  // `offer` property and allow us to remove it from the payload entirely.
  onSignalReady = (handler) => this.#registerSignalListeners(handler);

  /**
   * Signals the BAU (non-offer) flow and aborts any currently active API call.
   *
   * @returns {void}
   */
  signalBAU = () => {
    this.cancelActiveApiCall();
    this.#emit(FetchSignal.SIGNAL_NAME, { offer: false });
  };

  /**
   * Signals the offer flow and aborts any currently active API call.
   *
   * @returns {void}
   */
  signalOffer = () => {
    this.cancelActiveApiCall();
    this.#emit(FetchSignal.SIGNAL_NAME, { offer: true });
  };

  /**
   * Gets the active AbortController, creating one if it does not exist.
   *
   * @returns {AbortController} Active abort controller instance.
   */
  getActiveAbortController = () => {
    if (!this.currentAbortController) {
      this.currentAbortController = new AbortController();
    }
    return this.currentAbortController;
  };

  /**
   * Aborts and clears the active API call controller, if present.
   *
   * @returns {void}
   */
  cancelActiveApiCall = () => {
    if (this.currentAbortController) {
      this.currentAbortController.abort();
      this.currentAbortController = null;
    }
  };
}

export default FetchSignal;
